import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldAlert, Lock, X, LogOut, CheckCircle, AlertTriangle, Loader } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import LoginPage from './LoginPage'
import './SecureAccountPage.css'

/**
 * Crisis-mode landing page reached via the "Secure My Account" button in
 * the email-change heads-up notification. If signed out, defers to the
 * normal login form (with context). Once signed in, surfaces three
 * focused actions: cancel the pending email change, change password, and
 * sign out all other devices.
 */
function SecureAccountPage() {
  const { currentUser, userProfile, loading } = useAuth()

  if (loading) {
    return (
      <div className="secure-loading">
        <Loader className="spin" size={28} />
        <p>Loading your account…</p>
      </div>
    )
  }

  // Not signed in — show the login form with context. After login, this
  // route re-renders with currentUser set and shows the actions panel.
  if (!currentUser) {
    return (
      <div className="secure-login-wrap">
        <div className="secure-login-banner">
          <ShieldAlert size={20} />
          <div>
            <strong>Sign in to secure your account.</strong>
            <p>You're here because someone requested an email change. Sign in below to review and lock things down.</p>
          </div>
        </div>
        <LoginPage />
      </div>
    )
  }

  return <SecureAccountActions userProfile={userProfile} />
}

function SecureAccountActions({ userProfile }) {
  const { cancelPendingEmailChange, changePassword, signOutAllDevices, signOut } = useAuth()
  const navigate = useNavigate()

  const [cancelling, setCancelling] = useState(false)
  const [cancelDone, setCancelDone] = useState(false)
  const [cancelError, setCancelError] = useState('')

  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordWorking, setPasswordWorking] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordDone, setPasswordDone] = useState(false)

  const [signOutWorking, setSignOutWorking] = useState(false)
  const [signOutDone, setSignOutDone] = useState(false)
  const [signOutError, setSignOutError] = useState('')

  const hasPending = !!userProfile?.pendingEmail

  const handleCancel = async () => {
    setCancelError('')
    setCancelling(true)
    try {
      await cancelPendingEmailChange()
      setCancelDone(true)
    } catch (err) {
      setCancelError(err.message)
    } finally {
      setCancelling(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setPasswordError('')
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords don't match.")
      return
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.")
      return
    }
    setPasswordWorking(true)
    try {
      await changePassword(currentPassword, newPassword)
      setPasswordDone(true)
      setShowPasswordForm(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordError(err.message)
    } finally {
      setPasswordWorking(false)
    }
  }

  const handleSignOutAll = async () => {
    setSignOutError('')
    setSignOutWorking(true)
    try {
      await signOutAllDevices()
      setSignOutDone(true)
    } catch (err) {
      setSignOutError(err.message)
    } finally {
      setSignOutWorking(false)
    }
  }

  return (
    <div className="secure-page">
      <div className="secure-container">
        <header className="secure-header">
          <div className="secure-icon-wrap">
            <ShieldAlert size={32} />
          </div>
          <h1>Secure your account</h1>
          {userProfile?.email && (
            <p className="secure-signed-in-as">
              Signed in as <strong>{userProfile.email}</strong>
            </p>
          )}
          <p>
            We received an email-change request on your account. If that wasn't you, take the steps below right away.
            {hasPending && (
              <>
                {' '}A verification link was sent to <strong>{userProfile.pendingEmail}</strong> — the change won't take effect until that link is opened.
              </>
            )}
          </p>
        </header>

        <div className="secure-warning">
          <AlertTriangle size={16} />
          <span>
            <strong>Heads up:</strong> Cancelling the change clears it from your account, but if someone has the verification link in the new inbox, only changing your password fully prevents further account changes.
          </span>
        </div>

        {/* Action 1: Cancel pending email change */}
        <section className={`secure-action ${cancelDone ? 'done' : ''}`}>
          <div className="secure-action-icon">
            <X size={22} />
          </div>
          <div className="secure-action-body">
            <h2>Cancel the email change</h2>
            {cancelDone ? (
              <p className="secure-action-success">
                <CheckCircle size={16} /> Pending change cancelled.
              </p>
            ) : hasPending ? (
              <>
                <p>Removes the pending change from your account so it stops appearing on your profile.</p>
                {cancelError && <p className="secure-action-error">{cancelError}</p>}
                <button
                  className="secure-btn secondary"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling…' : 'Cancel email change'}
                </button>
              </>
            ) : (
              <p className="secure-action-muted">No pending email change found on your account.</p>
            )}
          </div>
        </section>

        {/* Action 2: Change password (primary) */}
        <section className={`secure-action primary ${passwordDone ? 'done' : ''}`}>
          <div className="secure-action-icon">
            <Lock size={22} />
          </div>
          <div className="secure-action-body">
            <h2>Change your password</h2>
            {passwordDone ? (
              <p className="secure-action-success">
                <CheckCircle size={16} /> Password updated.
              </p>
            ) : showPasswordForm ? (
              <form className="secure-password-form" onSubmit={handlePasswordSubmit}>
                <label>
                  Current password
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </label>
                <label>
                  New password
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </label>
                <label>
                  Confirm new password
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </label>
                {passwordError && <p className="secure-action-error">{passwordError}</p>}
                <div className="secure-password-actions">
                  <button
                    type="button"
                    className="secure-btn secondary"
                    onClick={() => {
                      setShowPasswordForm(false)
                      setPasswordError('')
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="secure-btn primary" disabled={passwordWorking}>
                    {passwordWorking ? 'Updating…' : 'Update password'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p>Stops anyone with your current password from making further changes. <strong>Recommended.</strong></p>
                <button className="secure-btn primary" onClick={() => setShowPasswordForm(true)}>
                  Change my password
                </button>
              </>
            )}
          </div>
        </section>

        {/* Action 3: Sign out all other devices */}
        <section className={`secure-action ${signOutDone ? 'done' : ''}`}>
          <div className="secure-action-icon">
            <LogOut size={22} />
          </div>
          <div className="secure-action-body">
            <h2>Sign out other devices</h2>
            {signOutDone ? (
              <p className="secure-action-success">
                <CheckCircle size={16} /> All other sessions revoked.
              </p>
            ) : (
              <>
                <p>Forces every other signed-in device to log back in. Your current session stays active.</p>
                {signOutError && <p className="secure-action-error">{signOutError}</p>}
                <button
                  className="secure-btn secondary"
                  onClick={handleSignOutAll}
                  disabled={signOutWorking}
                >
                  {signOutWorking ? 'Signing out…' : 'Sign out everywhere else'}
                </button>
              </>
            )}
          </div>
        </section>

        <div className="secure-footer">
          <button className="secure-btn link" onClick={() => navigate('/')}>
            Back to NeighborTopia
          </button>
          <button
            className="secure-btn link danger"
            onClick={async () => {
              await signOut()
              navigate('/login')
            }}
          >
            Sign out
          </button>
        </div>

        <p className="secure-help">
          Need help? Email <a href="mailto:support@neighbortopia.app">support@neighbortopia.app</a>.
        </p>
      </div>
    </div>
  )
}

export default SecureAccountPage
